import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import api from '../api/api';
import { setUserDetails } from '../redux/userSlice';
import { useDispatch } from 'react-redux';
import FieldLogin from './FieldLogin'
import Footer from './Footer';
import Toast from '../global/toastmessage';
import { sidebarListItems } from '../utils/sidebarList';
import { logActivity } from '../utils/activityLogger';

const OTP_LENGTH = 4;

const Login = () => {
  const [mode, setMode] = useState('otp'); // 'password' | 'otp'

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP state
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const otpRefs = useRef([]);

  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [showFieldLogin, setShowFieldLogin] = useState(false);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const showToast = (message, severity = 'success') =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  const handleCloseFieldLogin = () => {
    setShowFieldLogin(false);
  };

  const dispatch = useDispatch();

  const handleLoginSuccess = (response) => {
    sessionStorage.setItem('accessToken', response?.access_token);
    sessionStorage.setItem('role', response.data[0]?.user_roles?.role);
    sessionStorage.setItem('userid', response?.id);
    dispatch(setUserDetails(response?.data[0]));
    sidebarListItems(null);
    logActivity('LOGIN', { page: 'Login', target: mode === 'otp' ? 'OTP Login' : 'Password Login' });
    if (response.data[0]?.role == 1) {
      navigate('/home');
    } else if (response.data[0]?.role == 2) {
      navigate('/dashboard');
    } else {
      navigate('/home');
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setOtpSent(false);
    setOtp(Array(OTP_LENGTH).fill(''));
  };

  const submitLogin = async () => {
    if (!username) {
      setError('Username is required.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }

    setLoading(true);
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    try {
      const response = await api.post(`/auth/token`, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.status === 200) {
        handleLoginSuccess(response);
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (error) {
      setError('Login failed. Please check your details and try again.');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    if (!phone || phone.length < 10) {
      setError('Please enter a valid phone number.');
      return;
    }
    setError('');
    setSending(true);
    try {
      const response = await api.post('/auth/otp/send', { phone_number: phone });
      if (response.status === 200) {
        showToast('OTP sent successfully.', 'success');
        setOtpSent(true);
        setOtp(Array(OTP_LENGTH).fill(''));
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        setError(response.message || 'Unable to send OTP.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Phone number not found.');
    } finally {
      setSending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/, '');
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setError('');
    if (digit && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otp[index]) {
        handleOtpChange(index, '');
      } else if (index > 0) {
        otpRefs.current[index - 1]?.focus();
        const next = [...otp];
        next[index - 1] = '';
        setOtp(next);
      }
    }
  };

  const verifyOtp = async () => {
    const enteredOtp = otp.join('');
    if (enteredOtp.length !== OTP_LENGTH) {
      setError('Please enter the complete OTP.');
      return;
    }
    setError('');
    setVerifying(true);
    try {
      const response = await api.post('/auth/otp/verify', {
        phone_number: phone,
        otp: enteredOtp,
      });
      if (response.status === 200) {
        handleLoginSuccess(response);
      } else {
        setError(response.message || 'Invalid OTP.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Verification failed.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <div className="flex flex-grow items-center justify-center px-4">
        <div className="w-full max-w-[660px]">
          <div className="bg-white p-6 md:p-8 rounded-lg shadow-custom-dark w-full h-auto flex flex-col justify-center">
            <div className="flex justify-center my-8">
              <img
                src="https://ai.brainymed.com/ntact/public//assets/images/logo.png?id=2025030410"
                alt="Logo"
                className="w-50 h-auto"
              />
            </div>

            {mode === 'password' ? (
              <>
                <div className="text-md font-inter mb-1 text-start text-[#6c757d]">Enter Username</div>
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setError('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="text-start text-[#6c757d] text-md font-inter mb-1">Enter Password</div>
                <div className="relative w-full">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && submitLogin()}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>

                {error && <div className="text-red-600 text-sm mt-2 mb-2">{error}</div>}

                <button
                  onClick={submitLogin}
                  disabled={loading}
                  className={`w-full px-3 py-2 mt-8 text-white rounded-md text-md font-inter transition duration-300 ${
                    loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#145388] hover:bg-[#0f3a66]'
                  }`}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>

                <div className="mt-3 text-right">
                  <button
                    type="button"
                    onClick={() => switchMode('otp')}
                    className="text-sm font-inter text-[#145388] hover:underline"
                  >
                    Login with OTP
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-md font-inter mb-1 text-start text-[#6c757d]">Enter Phone Number</div>
                <div className="relative w-full">
                  <input
                    type="tel"
                    inputMode="numeric"
                    placeholder="Phone Number"
                    maxLength={15}
                    value={phone}
                    disabled={otpSent}
                    onChange={(e) => {
                      setPhone(e.target.value.replace(/\D/, ''));
                      setError('');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && !otpSent && sendOtp()}
                    className={`w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      otpSent ? 'bg-gray-100 pr-16' : ''
                    }`}
                  />
                  {otpSent && (
                    <button
                      type="button"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp(Array(OTP_LENGTH).fill(''));
                        setError('');
                      }}
                      className="absolute inset-y-0 right-3 flex items-center text-sm text-[#145388] hover:underline"
                    >
                      Change
                    </button>
                  )}
                </div>

                {!otpSent ? (
                  <>
                    {error && <div className="text-red-600 text-sm mt-2 mb-2">{error}</div>}
                    <button
                      onClick={sendOtp}
                      disabled={sending}
                      className={`w-full px-3 py-2 mt-8 text-white rounded-md text-md font-inter transition duration-300 ${
                        sending ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#145388] hover:bg-[#0f3a66]'
                      }`}
                    >
                      {sending ? 'Sending OTP...' : 'Send OTP'}
                    </button>

                    <div className="mt-3 text-right">
                      <button
                        type="button"
                        onClick={() => switchMode('password')}
                        className="text-sm font-inter text-[#145388] hover:underline"
                      >
                        Login with Password
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-md font-inter mb-2 mt-6 text-start text-[#6c757d]">
                      Enter the {OTP_LENGTH}-digit OTP
                    </div>
                    <div className="flex gap-3">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => (otpRefs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className="h-14 w-14 rounded-lg border border-gray-300 text-center text-2xl font-semibold focus:outline-none focus:ring-2 focus:ring-[#145388]"
                        />
                      ))}
                    </div>

                    {error && <div className="text-red-600 text-sm mt-2 mb-2">{error}</div>}

                    <button
                      onClick={verifyOtp}
                      disabled={verifying}
                      className={`w-full px-3 py-2 mt-8 text-white rounded-md text-md font-inter transition duration-300 ${
                        verifying ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#145388] hover:bg-[#0f3a66]'
                      }`}
                    >
                      {verifying ? 'Verifying...' : 'Verify & Login'}
                    </button>

                    <div className="mt-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => switchMode('password')}
                        className="text-sm font-inter text-[#145388] hover:underline"
                      >
                        Login with Password
                      </button>
                      <button
                        type="button"
                        onClick={sendOtp}
                        disabled={sending}
                        className="text-sm font-inter text-[#145388] hover:underline disabled:text-gray-400"
                      >
                        {sending ? 'Resending...' : 'Resend OTP'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            <div className="mt-3 text-right">
              {showFieldLogin && (
                <div className="fixed inset-0 z-50">
                  <FieldLogin onClose={handleCloseFieldLogin} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />

      <Toast
        open={toast.open}
        onClose={handleCloseToast}
        severity={toast.severity}
        message={toast.message}
      />
    </div>
  );
};

export default Login;
